import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { AuthState } from '@/common/types/auth.types';
import type {
  AssessmentListQuery,
  AssessmentListResponse,
  AssessmentApiResponse,
  AssessmentStatisticsQuery,
  AssessmentStatisticsResponse,
} from '@/common/types/assessment.types';
import type {
  ARFormData,
  ConstructionType,
  OccupancyType,
  DamagePercentage,
  DetailedEvalType,
} from '@/pages/AssessmentRapid/ARForm/ARForm.types';
import type {
  ADFormData,
  HazardSeverityRecord,
} from '@/pages/AssessmentDetailed/ADForm/ADForm.types';

/**
 * Generate a unique assessment UUID
 */
function generateAssessmentUuid(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Map hazard severity from form (lowercase) to API format (uppercase)
 */
function mapSeverityToApi(severity: string | undefined): string | null {
  if (!severity) return null;
  const severityMap: Record<string, string> = {
    none: 'NONE',
    minor: 'MINOR',
    moderate: 'MODERATE',
    severe: 'SEVERE',
  };
  return severityMap[severity] || null;
}

/**
 * Map detailed damage percentage from form to API ENUM format
 * Database ENUM: NONE, ZERO_TO_ONE, ONE_TO_TEN, TEN_TO_THIRTY, THIRTY_TO_SIXTY, SIXTY_TO_HUNDRED, HUNDRED
 */
function mapDetailedDamageToApi(damage: string | undefined): string | null {
  if (!damage) return null;
  const damageMap: Record<string, string> = {
    none: 'NONE',
    '0_1': 'ZERO_TO_ONE',
    '1_10': 'ONE_TO_TEN',
    '10_30': 'TEN_TO_THIRTY',
    '30_60': 'THIRTY_TO_SIXTY',
    '60_100': 'SIXTY_TO_HUNDRED',
    '100': 'HUNDRED',
  };
  return damageMap[damage] || 'NONE';
}

/**
 * Transform hazard severity records from form to API format (snake_case)
 */
function transformHazardsToApi(hazards: HazardSeverityRecord[]): Array<{
  hazard_type: string;
  severity: string | null;
  comments?: string | null;
}> {
  return hazards.map((h) => ({
    hazard_type: h.hazardType,
    severity: mapSeverityToApi(h.severity),
    comments: h.comments || null,
    other_description: h.otherDescription || null,
  }));
}

/**
 * Transform form data to API payload format (snake_case)
 * Supports both rapid (ARFormData) and detailed (ADFormData) modes
 */
function transformFormToApiPayload(
  data: ARFormData,
  incidentId: number,
  groupId: number,
  userId: number,
  existingUuid?: string
) {
  // Map posting status to placard status
  const placardStatusMap: Record<string, string> = {
    inspected: 'INSPECTED',
    restricted_use: 'RESTRICTED',
    unsafe: 'UNSAFE',
  };

  const now = new Date().toISOString();
  const isDetailed = (data as ADFormData).assessment_mode === 'detailed';
  const detailedData = data as ADFormData;

  const payload: Record<string, unknown> = {
    // Identity fields (REQUIRED)
    assessment_uuid: existingUuid || data.id || generateAssessmentUuid(),
    parcel_id: data.parcelId || '',
    incident_id: incidentId,
    group_id: groupId,
    assessment_mode: isDetailed ? 'detailed' : 'rapid',
    // Inspector info
    inspector_id: data.inspectorId,
    affiliation: data.affiliation || null,
    inspected_at: data.inspectionDateTime,
    areas_inspected: data.areasInspected?.toUpperCase() || 'EXTERIOR_ONLY',
    // Address fields
    address: data.address1,
    address2: data.address2 || null,
    city: data.city,
    county: data.county,
    state: data.state,
    zip_code: data.zipcode,
    latitude: data.latitude || null,
    longitude: data.longitude || null,
    // Building info (API expects UPPERCASE)
    construction_type: data.typeOfConstruction?.toUpperCase() || null,
    occupancy_type: data.primaryOccupancy?.toUpperCase() || null,
    // Building detail fields
    contact_phone: data.contactPhone || null,
    stories_above_ground: data.storiesAboveGround || null,
    stories_below_ground: data.storiesBelowGround || null,
    building_footprint: data.buildingFootprint || null,
    total_residential_units: data.residentialUnits || null,
    inhabitable_residential_units: data.inhabitableResidentialUnits || null,
    // Overall hazards (individual fields, not nested object)
    overall_hazard_collapse: mapSeverityToApi(data.hazards?.collapseOrOffFoundation),
    overall_hazard_building_leaning: mapSeverityToApi(data.hazards?.buildingOrStoryLeaning),
    overall_hazard_racking_damage: mapSeverityToApi(data.hazards?.rackingDamageWalls),
    overall_hazard_chimney_parapet: mapSeverityToApi(data.hazards?.chimneyParapetFallingHazard),
    overall_hazard_ground_slope: mapSeverityToApi(data.hazards?.groundSlopeMovement),
    overall_hazard_other: mapSeverityToApi(data.hazards?.otherSeverity),
    overall_hazard_other_description: data.hazards?.otherDescription || null,
    // Evaluation
    damage_extent_overall: mapDetailedDamageToApi(data.estimatedBuildingDamage),
    evaluation_notes: data.comments || null,
    // Posting
    placard_status: (data.postingStatus && placardStatusMap[data.postingStatus]) || 'NOT_ASSESSED',
    recommendations: data.restrictionsText || null,
    detailed_evaluation_needed: data.detailedEvalRecommended,
    detailed_eval_types: data.detailedEvalTypes || null,
    // Further Actions
    barricades_needed: data.barricadesNeeded || false,
    barricade_locations: data.barricadeLocations || null,
    engineering_eval_recommended: data.engineeringEvalRecommended || false,
    engineering_eval_structural: data.engineeringEvalStructural || false,
    engineering_eval_geotechnical: data.engineeringEvalGeotechnical || false,
    engineering_eval_other: data.engineeringEvalOther || null,
    actions_comments: data.actionsComments || null,
    // Photos stored as JSON (Note: full upload requires presign flow like PWA)
    photos: data.photos || null,
    // Audit fields (REQUIRED) - API expects strings
    created_by: String(userId),
    updated_by: String(userId),
    client_created_at: now,
    client_updated_at: now,
  };

  // Add detailed-specific fields when in detailed mode
  if (isDetailed) {
    // Override damage field: detailed uses estimated_building_damage (7-level) instead of damage_extent_overall
    delete payload.damage_extent_overall;
    payload.estimated_building_damage = mapDetailedDamageToApi(data.estimatedBuildingDamage);

    // Extended building fields
    payload.building_name = detailedData.buildingName || null;
    payload.contact_phone = detailedData.contactPhone || null;
    payload.stories_above_ground = detailedData.storiesAboveGround ?? null;
    payload.stories_below_ground = detailedData.storiesBelowGround ?? null;
    payload.building_footprint = detailedData.buildingFootprint ?? null;
    payload.number_of_units = detailedData.numberOfUnits ?? null;
    payload.total_residential_units = detailedData.totalResidentialUnits ?? null;
    payload.inhabitable_residential_units = detailedData.inhabitableResidentialUnits ?? null;

    // Detailed hazard arrays
    payload.structural_hazards = transformHazardsToApi(detailedData.structuralHazards || []);
    payload.non_structural_hazards = transformHazardsToApi(detailedData.nonStructuralHazards || []);
    payload.geotechnical_hazards = transformHazardsToApi(detailedData.geotechnicalHazards || []);

    // Actions section
    payload.barricades_needed = detailedData.barricadesNeeded || false;
    payload.barricade_locations = detailedData.barricadeLocations || null;
    payload.engineering_eval_recommended = detailedData.engineeringEvalRecommended || false;
    payload.engineering_eval_structural = detailedData.engineeringEvalStructural || false;
    payload.engineering_eval_geotechnical = detailedData.engineeringEvalGeotechnical || false;
    payload.engineering_eval_other = detailedData.engineeringEvalOther || null;
    payload.other_recommendations = detailedData.otherRecommendations || null;
    payload.other_recommendations_specify = detailedData.otherRecommendationsSpecify || null;
    payload.actions_comments = detailedData.actionsComments || null;

    // Overall hazard comments (AD-specific)
    payload.overall_hazard_collapse_comments = detailedData.overallHazardCollapseComments || null;
    payload.overall_hazard_other_comments = detailedData.overallHazardOtherComments || null;

    // General comments
    payload.general_comments = detailedData.generalComments || null;

    // Previous posting
    payload.previous_posting_inspected = detailedData.previousPostingInspected || false;
    payload.previous_posting_restricted = detailedData.previousPostingRestricted || false;
    payload.previous_posting_unsafe = detailedData.previousPostingUnsafe || false;
    payload.previous_inspector_id = detailedData.previousInspectorId || null;
    payload.previous_posting_date = detailedData.previousPostingDate || null;
  }

  return payload;
}

/**
 * Map API severity (uppercase) to form format (lowercase)
 * NONE maps to 'minor' (Minor/None) since the None option was removed
 */
function mapSeverityFromApi(
  severity: string | null | undefined
): 'minor' | 'moderate' | 'severe' {
  if (!severity) return 'minor';
  const severityMap: Record<string, 'minor' | 'moderate' | 'severe'> = {
    NONE: 'minor',
    MINOR: 'minor',
    MODERATE: 'moderate',
    SEVERE: 'severe',
  };
  return severityMap[severity.toUpperCase()] || 'minor';
}

/**
 * Map API damage severity to form format (7-level scale)
 * Handles both old 4-level ENUM (NONE, MINOR, MODERATE, SEVERE) and
 * new 7-level ENUM (NONE, ZERO_TO_ONE, ONE_TO_TEN, TEN_TO_THIRTY, THIRTY_TO_SIXTY, SIXTY_TO_HUNDRED, HUNDRED)
 */
function mapDamageFromApi(damage: string | null | undefined): DamagePercentage {
  if (!damage) return 'none';
  const damageMap: Record<string, DamagePercentage> = {
    NONE: 'none',
    ZERO_TO_ONE: '0_1',
    ONE_TO_TEN: '1_10',
    TEN_TO_THIRTY: '10_30',
    THIRTY_TO_SIXTY: '30_60',
    SIXTY_TO_HUNDRED: '60_100',
    HUNDRED: '100',
    // Backward compat: old 4-level values map to closest new values
    MINOR: '0_1',
    MODERATE: '1_10',
    SEVERE: '10_30',
  };
  return damageMap[damage.toUpperCase()] || 'none';
}

/**
 * Map API detailed damage ENUM to form format
 * Database ENUM: NONE, ZERO_TO_ONE, ONE_TO_TEN, TEN_TO_THIRTY, THIRTY_TO_SIXTY, SIXTY_TO_HUNDRED, HUNDRED
 */
function mapDetailedDamageFromApi(damage: string | null | undefined): string {
  if (!damage) return 'none';
  const damageMap: Record<string, string> = {
    NONE: 'none',
    ZERO_TO_ONE: '0_1',
    ONE_TO_TEN: '1_10',
    TEN_TO_THIRTY: '10_30',
    THIRTY_TO_SIXTY: '30_60',
    SIXTY_TO_HUNDRED: '60_100',
    HUNDRED: '100',
  };
  return damageMap[damage.toUpperCase()] || 'none';
}

/**
 * Map API areas_inspected (UPPERCASE) to form format (lowercase)
 */
function mapAreasInspectedFromApi(
  value: string | undefined
): 'exterior_only' | 'exterior_and_interior' {
  if (!value) return 'exterior_only';
  const lower = value.toLowerCase();
  return lower === 'exterior_and_interior' ? 'exterior_and_interior' : 'exterior_only';
}

/**
 * Map API construction_type (UPPERCASE) to form format (lowercase)
 */
function mapConstructionTypeFromApi(value: string | undefined): ConstructionType {
  if (!value) return 'wood_frame';
  return value.toLowerCase() as ConstructionType;
}

/**
 * Map API occupancy_type (UPPERCASE) to form format (lowercase)
 */
function mapOccupancyTypeFromApi(value: string | undefined): OccupancyType {
  if (!value) return 'residential';
  return value.toLowerCase() as OccupancyType;
}

/**
 * Transform API hazard records to form HazardSeverityRecord format
 */
function transformHazardsFromApi(
  apiHazards: ApiHazardRecord[] | undefined
): HazardSeverityRecord[] {
  if (!apiHazards || apiHazards.length === 0) return [];
  return apiHazards.map((h) => ({
    hazardType: h.hazard_type,
    severity: h.severity ? mapSeverityFromApi(h.severity) : undefined,
    comments: h.comments || undefined,
    otherDescription: h.other_description || undefined,
  }));
}

/**
 * Transform API response to ARFormData format (camelCase)
 * Returns ADFormData when assessment_mode is 'detailed'
 */
function transformApiToFormData(api: AssessmentDetailResponse): ARFormData | ADFormData {
  // Map placard status back to posting status
  const postingStatusMap: Record<string, 'inspected' | 'restricted_use' | 'unsafe'> = {
    INSPECTED: 'inspected',
    RESTRICTED: 'restricted_use',
    UNSAFE: 'unsafe',
  };

  const baseData: ARFormData = {
    id: api.assessment_uuid,
    parcelId: api.parcel_id || '',
    inspectorId: api.inspector_id,
    affiliation: api.affiliation || '',
    inspectionDateTime: api.inspected_at,
    areasInspected: mapAreasInspectedFromApi(api.areas_inspected),
    address1: api.address || '',
    address2: api.address2 || undefined,
    city: api.city || '',
    county: api.county || '',
    state: api.state || '',
    zipcode: api.zip_code || '',
    latitude: api.latitude || undefined,
    longitude: api.longitude || undefined,
    typeOfConstruction: mapConstructionTypeFromApi(api.construction_type),
    primaryOccupancy: mapOccupancyTypeFromApi(api.occupancy_type),
    contactPhone: api.contact_phone || undefined,
    storiesAboveGround: api.stories_above_ground != null ? String(api.stories_above_ground) : undefined,
    storiesBelowGround: api.stories_below_ground != null ? String(api.stories_below_ground) : undefined,
    buildingFootprint: api.building_footprint != null ? String(api.building_footprint) : undefined,
    residentialUnits: api.total_residential_units != null ? String(api.total_residential_units) : undefined,
    inhabitableResidentialUnits: api.inhabitable_residential_units != null ? String(api.inhabitable_residential_units) : undefined,
    hazards: {
      collapseOrOffFoundation: mapSeverityFromApi(api.overall_hazard_collapse),
      buildingOrStoryLeaning: mapSeverityFromApi(api.overall_hazard_building_leaning),
      rackingDamageWalls: mapSeverityFromApi(api.overall_hazard_racking_damage),
      chimneyParapetFallingHazard: mapSeverityFromApi(api.overall_hazard_chimney_parapet),
      groundSlopeMovement: mapSeverityFromApi(api.overall_hazard_ground_slope),
      otherSeverity: api.overall_hazard_other
        ? mapSeverityFromApi(api.overall_hazard_other)
        : undefined,
      otherDescription: api.overall_hazard_other_description || undefined,
    },
    estimatedBuildingDamage: mapDamageFromApi(api.damage_extent_overall),
    comments: api.evaluation_notes || undefined,
    postingStatus: postingStatusMap[api.placard_status] || 'inspected',
    restrictionsText: api.recommendations || undefined,
    detailedEvalRecommended: api.detailed_evaluation_needed || false,
    detailedEvalTypes: api.detailed_eval_types as DetailedEvalType[] | undefined,
    barricadesNeeded: api.barricades_needed || false,
    barricadeLocations: api.barricade_locations || undefined,
    engineeringEvalRecommended: api.engineering_eval_recommended || false,
    engineeringEvalStructural: api.engineering_eval_structural || false,
    engineeringEvalGeotechnical: api.engineering_eval_geotechnical || false,
    engineeringEvalOther: api.engineering_eval_other || undefined,
    actionsComments: api.actions_comments || undefined,
    photos: api.photos || undefined,
  };

  // Return extended ADFormData when assessment is detailed mode
  if (api.assessment_mode === 'detailed') {
    return {
      ...baseData,
      assessment_mode: 'detailed',
      // Override: detailed uses estimated_building_damage (7-level) instead of damage_extent_overall
      estimatedBuildingDamage: mapDetailedDamageFromApi(api.estimated_building_damage),
      // Extended building fields
      buildingName: api.building_name || undefined,
      contactPhone: api.contact_phone || undefined,
      storiesAboveGround: api.stories_above_ground ?? undefined,
      storiesBelowGround: api.stories_below_ground ?? undefined,
      buildingFootprint: api.building_footprint ?? undefined,
      numberOfUnits: api.number_of_units ?? undefined,
      totalResidentialUnits: api.total_residential_units ?? undefined,
      inhabitableResidentialUnits: api.inhabitable_residential_units ?? undefined,
      // Detailed hazard arrays
      structuralHazards: transformHazardsFromApi(api.structural_hazards),
      nonStructuralHazards: transformHazardsFromApi(api.non_structural_hazards),
      geotechnicalHazards: transformHazardsFromApi(api.geotechnical_hazards),
      // Actions
      barricadesNeeded: api.barricades_needed || false,
      barricadeLocations: api.barricade_locations || undefined,
      engineeringEvalRecommended: api.engineering_eval_recommended || false,
      engineeringEvalStructural: api.engineering_eval_structural || false,
      engineeringEvalGeotechnical: api.engineering_eval_geotechnical || false,
      engineeringEvalOther: api.engineering_eval_other || undefined,
      otherRecommendations: api.other_recommendations || undefined,
      otherRecommendationsSpecify: api.other_recommendations_specify || undefined,
      actionsComments: api.actions_comments || undefined,
      // Overall hazard comments (AD-specific)
      overallHazardCollapseComments: api.overall_hazard_collapse_comments || undefined,
      overallHazardOtherComments: api.overall_hazard_other_comments || undefined,
      // General comments
      generalComments: api.general_comments || undefined,
      // Previous posting
      previousPostingInspected: api.previous_posting_inspected || false,
      previousPostingRestricted: api.previous_posting_restricted || false,
      previousPostingUnsafe: api.previous_posting_unsafe || false,
      previousInspectorId: api.previous_inspector_id || undefined,
      previousPostingDate: api.previous_posting_date || undefined,
    } as ADFormData;
  }

  return baseData;
}

/**
 * API hazard record format (snake_case)
 */
interface ApiHazardRecord {
  hazard_type: string;
  severity?: string | null;
  comments?: string | null;
  other_description?: string | null;
}

/**
 * Extended API response for single assessment with full details
 */
interface AssessmentDetailResponse extends AssessmentApiResponse {
  affiliation?: string;
  areas_inspected?: string;
  address2?: string;
  county?: string;
  state?: string;
  zip_code?: string;
  construction_type?: string;
  occupancy_type?: string;
  // Individual hazard fields from API
  overall_hazard_collapse?: string;
  overall_hazard_building_leaning?: string;
  overall_hazard_racking_damage?: string;
  overall_hazard_chimney_parapet?: string;
  overall_hazard_ground_slope?: string;
  overall_hazard_other?: string;
  overall_hazard_other_description?: string;
  // Evaluation
  damage_extent_overall?: string;
  estimated_building_damage?: string;
  evaluation_notes?: string;
  // Posting
  recommendations?: string;
  detailed_evaluation_needed?: boolean;
  detailed_eval_types?: string[];
  photos?: ARFormData['photos'];
  // Detailed assessment fields
  building_name?: string;
  contact_phone?: string;
  stories_above_ground?: number;
  stories_below_ground?: number;
  building_footprint?: number;
  number_of_units?: number;
  total_residential_units?: number;
  inhabitable_residential_units?: number;
  // Detailed hazard arrays
  structural_hazards?: ApiHazardRecord[];
  non_structural_hazards?: ApiHazardRecord[];
  geotechnical_hazards?: ApiHazardRecord[];
  // Actions
  barricades_needed?: boolean;
  barricade_locations?: string;
  engineering_eval_recommended?: boolean;
  engineering_eval_structural?: boolean;
  engineering_eval_geotechnical?: boolean;
  engineering_eval_other?: string;
  other_recommendations?: string[];
  other_recommendations_specify?: string;
  actions_comments?: string;
  // Overall hazard comments (AD-specific)
  overall_hazard_collapse_comments?: string;
  overall_hazard_other_comments?: string;
  // General comments
  general_comments?: string;
  // Previous posting
  previous_posting_inspected?: boolean;
  previous_posting_restricted?: boolean;
  previous_posting_unsafe?: boolean;
  previous_inspector_id?: string;
  previous_posting_date?: string;
}

/**
 * Payload for creating/updating assessments
 */
interface SaveAssessmentPayload {
  data: ARFormData;
  incidentId: number;
  groupId: number;
  userId: number;
  uuid?: string; // For updates
}

const baseURL = import.meta.env.VITE_API_EDMA_BASE_URL;

/**
 * RTK Query API service for earthquake assessments
 *
 * Endpoints:
 * - getAssessments: Fetch paginated list of assessments with search/filter support
 * - getAssessmentByUuid: Fetch single assessment by UUID
 */
export const assessmentsApi = createApi({
  reducerPath: 'assessmentsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseURL}/api/earthquake`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
      if (token) {
        headers.set('Authorization', token);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['Assessment', 'AssessmentList', 'AssessmentStats'],
  endpoints: (builder) => ({
    /**
     * Get paginated list of assessments
     *
     * Required: incident_id, group_id
     * Optional: search, status, mode, limit, offset, sort, order
     */
    getAssessments: builder.query<AssessmentListResponse, AssessmentListQuery>({
      query: (params) => {
        const searchParams = new URLSearchParams();

        // Required params
        searchParams.set('incident_id', String(params.incident_id));
        searchParams.set('group_id', String(params.group_id));

        // Optional params
        if (params.search) {
          searchParams.set('search', params.search);
        }
        if (params.status) {
          searchParams.set('status', params.status);
        }
        if (params.mode) {
          searchParams.set('mode', params.mode);
        }
        if (params.inspector_id) {
          searchParams.set('inspector_id', params.inspector_id);
        }
        if (params.parcel_id) {
          searchParams.set('parcel_id', params.parcel_id);
        }
        if (params.sort) {
          searchParams.set('sort', params.sort);
        }
        if (params.order) {
          searchParams.set('order', params.order);
        }
        if (params.limit !== undefined) {
          searchParams.set('limit', String(params.limit));
        }
        if (params.offset !== undefined) {
          searchParams.set('offset', String(params.offset));
        }

        return {
          url: `/assessments?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.dataset.map(({ assessment_uuid }) => ({
                type: 'Assessment' as const,
                id: assessment_uuid,
              })),
              { type: 'AssessmentList', id: 'LIST' },
            ]
          : [{ type: 'AssessmentList', id: 'LIST' }],
    }),

    /**
     * Get single assessment by UUID with full details
     */
    getAssessmentByUuid: builder.query<AssessmentDetailResponse, string>({
      query: (uuid) => ({
        url: `/assessments/${uuid}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, uuid) => [{ type: 'Assessment', id: uuid }],
    }),

    /**
     * Get assessment as ARFormData (transformed)
     */
    getAssessmentForForm: builder.query<ARFormData | ADFormData, string>({
      query: (uuid) => ({
        url: `/assessments/${uuid}`,
        method: 'GET',
      }),
      transformResponse: (response: AssessmentDetailResponse) => transformApiToFormData(response),
      providesTags: (_result, _error, uuid) => [{ type: 'Assessment', id: uuid }],
    }),

    /**
     * Get aggregated assessment statistics by status and mode
     */
    getAssessmentStatistics: builder.query<AssessmentStatisticsResponse, AssessmentStatisticsQuery>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set('incident_id', String(params.incident_id));
        searchParams.set('group_id', String(params.group_id));
        if (params.county) {
          searchParams.set('county', params.county);
        }
        return {
          url: `/assessments/statistics?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['AssessmentStats'],
    }),

    /**
     * Create new assessment
     */
    createAssessment: builder.mutation<AssessmentApiResponse, SaveAssessmentPayload>({
      query: ({ data, incidentId, groupId, userId }) => ({
        url: '/assessments',
        method: 'POST',
        body: transformFormToApiPayload(data, incidentId, groupId, userId),
      }),
      invalidatesTags: ['AssessmentList', 'AssessmentStats'],
    }),

    /**
     * Update existing assessment
     */
    updateAssessment: builder.mutation<AssessmentApiResponse, SaveAssessmentPayload>({
      query: ({ data, incidentId, groupId, userId, uuid }) => ({
        url: `/assessments/${uuid}`,
        method: 'PUT',
        body: transformFormToApiPayload(data, incidentId, groupId, userId, uuid),
      }),
      invalidatesTags: (_result, _error, { uuid }) => [
        'AssessmentList',
        'AssessmentStats',
        { type: 'Assessment', id: uuid },
      ],
    }),

    /**
     * Delete assessment by UUID
     */
    deleteAssessment: builder.mutation<{ success: boolean }, string>({
      query: (uuid) => ({
        url: `/assessments/${uuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AssessmentList', 'AssessmentStats'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetAssessmentsQuery,
  useGetAssessmentByUuidQuery,
  useGetAssessmentForFormQuery,
  useGetAssessmentStatisticsQuery,
  useCreateAssessmentMutation,
  useUpdateAssessmentMutation,
  useDeleteAssessmentMutation,
} = assessmentsApi;
